import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaLock, FaPhone, FaTimesCircle, FaUser, FaLightbulb, FaShieldAlt, FaArrowLeft } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function UserLogin() {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/user/login`, {
        mobile,
        password
      });

      const { access_token, first_login, user_info } = response.data;
      
      // Save token and user info
      localStorage.setItem('userToken', access_token);
      localStorage.setItem('userInfo', JSON.stringify(user_info));
      
      // Redirect to dashboard
      if (first_login) {
        navigate('/user/dashboard?first_login=true');
      } else {
        navigate('/user/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle different error types
      if (error.response) {
        // Server responded with error
        const errorMessage = error.response.data?.detail || 'خطا در ورود به سیستم';
        setLoginError(errorMessage);
      } else if (error.request) {
        // Request made but no response
        setLoginError('خطا در برقراری ارتباط با سرور. لطفاً اتصال اینترنت خود را بررسی کنید.');
      } else {
        // Something else happened
        setLoginError('خطای غیرمنتظره رخ داده است. لطفاً دوباره تلاش کنید.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900 flex items-center justify-center p-4 pt-24 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-primary-400/20 to-secondary-400/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-accent-400/20 to-primary-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-primary-300/10 to-secondary-300/10 rounded-full blur-3xl"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-10 w-16 h-16 bg-gradient-to-br from-primary-500/30 to-primary-600/30 rounded-2xl backdrop-blur-sm"
        />
        <motion.div
          animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute top-40 right-20 w-12 h-12 bg-gradient-to-br from-secondary-500/30 to-secondary-600/30 rounded-xl backdrop-blur-sm"
        />
        <motion.div
          animate={{ y: [0, 15, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-32 left-20 w-10 h-10 bg-gradient-to-br from-accent-500/30 to-accent-600/30 rounded-lg backdrop-blur-sm"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-lg relative z-10"
      >
        {/* Main Card */}
        <div className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-2xl rounded-[2rem] shadow-2xl overflow-hidden border border-white/50 dark:border-gray-700/50">
          {/* Header with Logo */}
          <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-700 dark:via-primary-800 dark:to-primary-900 p-8 text-center overflow-hidden">
            {/* Header Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>
            
            <div className="relative z-10">
              {/* Logo Container */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative inline-block mb-4"
              >
                <div className="w-24 h-24 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center mx-auto shadow-2xl border-2 border-white/30">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_500dd289-4b7e-4ffb-9065-26d7bb62264f/artifacts/9xom66md_Logo%20NOAFARIN%20%20SHO.jpg"
                    alt="لوگو نوآفرین‌شو"
                    className="h-16 w-16 object-contain rounded-lg"
                  />
                </div>
                {/* Glowing Ring */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-secondary-400/50 to-accent-400/50 blur-xl scale-110 -z-10"></div>
              </motion.div>

              <motion.h1 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-3xl font-bold text-white mb-2"
              >
                ورود به پنل کاربری
              </motion.h1>
              <motion.p 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="text-white/80 text-lg"
              >
                سامانه نوآفرین‌شو
              </motion.p>
            </div>
          </div>

          {/* Form Section */}
          <div className="p-8">
            {/* Error Alert */}
            {loginError && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="mb-6 p-4 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 border-2 border-red-400 dark:border-red-600 rounded-2xl flex items-center gap-3"
              >
                <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FaTimesCircle className="text-xl text-white" />
                </div>
                <span className="font-semibold text-sm text-red-800 dark:text-red-300">{loginError}</span>
              </motion.div>
            )}

            <form onSubmit={handleLogin} className="space-y-6">
              {/* Mobile Input */}
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.5 }}
              >
                <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm">
                  شماره موبایل
                </label>
                <div className="relative group">
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    required
                    placeholder="09123456789"
                    dir="ltr"
                    className="w-full p-4 pl-16 bg-gray-50/80 dark:bg-gray-700/80 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-600 rounded-2xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 text-gray-800 dark:text-gray-200 transition-all duration-300 text-lg font-semibold text-right"
                    data-testid="user-mobile"
                  />
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg group-focus-within:scale-110 transition-transform duration-300">
                    <FaPhone className="text-white" />
                  </div>
                </div>
              </motion.div>

              {/* Password Input */}
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.6 }}
              >
                <label className="block mb-2 font-bold text-gray-700 dark:text-gray-300 text-sm">
                  رمز عبور
                </label>
                <div className="relative group">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="رمز عبور خود را وارد کنید"
                    className="w-full p-4 pr-12 bg-gray-50/80 dark:bg-gray-700/80 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-600 rounded-2xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 text-gray-800 dark:text-gray-200 transition-all duration-300 text-lg"
                    data-testid="user-password"
                  />
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg group-focus-within:scale-110 transition-transform duration-300">
                    <FaLock className="text-white" />
                  </div>
                </div>
              </motion.div>

              {/* Help Box */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.7 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-5 border-2 border-blue-200/50 dark:border-blue-700/50"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <FaLightbulb className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 dark:text-gray-200 mb-2">راهنمای ورود</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      شماره موبایلی که در جشنواره امید ثبت‌نام کرده‌اید را وارد کنید.
                    </p>
                    <div className="mt-3 flex items-center gap-2 bg-white/60 dark:bg-gray-800/60 rounded-xl px-4 py-2">
                      <FaShieldAlt className="text-secondary-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        رمز عبور پیش‌فرض: <span className="font-mono font-bold text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 px-2 py-1 rounded-lg">12345678</span>
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={loading}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.8 }}
                whileHover={{ scale: loading ? 1 : 1.02, boxShadow: "0 20px 40px rgba(37, 99, 235, 0.3)" }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                className={`w-full py-5 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-600 text-white rounded-2xl font-bold text-lg shadow-xl transition-all duration-300 relative overflow-hidden ${
                  loading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-2xl'
                }`}
                data-testid="user-login-btn"
              >
                {/* Button Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>
                
                {loading ? (
                  <span className="flex items-center justify-center gap-3 relative z-10">
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                    در حال ورود...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-3 relative z-10">
                    <FaUser className="text-xl" />
                    ورود به سامانه
                  </span>
                )}
              </motion.button>

              {/* Register Link */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.9 }}
                className="text-center pt-4"
              >
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  هنوز در جشنواره ثبت‌نام نکرده‌اید؟
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/festival')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 text-gray-900 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <span>ثبت‌نام در جشنواره</span>
                  <FaArrowLeft />
                </button>
              </motion.div>
            </form>
          </div>
        </div>

        {/* Bottom Branding */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
          className="text-center mt-6"
        >
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            مرکز رشد و کارآفرینی بین‌المللی دانشگاه شمال
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default UserLogin;
